/**
 * ListingSpotPanel - Listing creation form embedded in SpotMode sidebar.
 *
 * Rendered by SpotModeUI when spotVariant === 'listing'. The pickup location
 * comes from the dropped SpotMode pin (location prop). The user fills in
 * listing details and submits — no separate PostListingPanel modal needed.
 *
 * @atomic @idempotent
 */
import { memo, useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FireIcon,
  ShoppingBagIcon,
  WrenchScrewdriverIcon,
  HeartIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { listingAPI } from '../../services/api/listingAPI'
import { reverseGeocode } from '../../services/geocoding'
import { getInputClasses, labelClasses, getTextareaClasses } from '../../styles/inputStyles'
import { toast } from '../../services/toastService'
import { createLogger } from '../../utils/logger'

const logger = createLogger('ListingSpotPanel')

const CATEGORIES = [
  { id: 'food',       labelKey: 'listings.category.food',      Icon: FireIcon },
  { id: 'goods',      labelKey: 'listings.category.goods',     Icon: ShoppingBagIcon },
  { id: 'service',    labelKey: 'listings.category.service',   Icon: WrenchScrewdriverIcon },
  { id: 'mutual_aid', labelKey: 'listings.category.mutualAid', Icon: HeartIcon },
]

const EXPIRY_OPTIONS = [
  { value: '1h',  labelKey: 'listings.expiry.1h',  label: '1 hour'   },
  { value: '4h',  labelKey: 'listings.expiry.4h',  label: '4 hours'  },
  { value: '8h',  labelKey: 'listings.expiry.8h',  label: '8 hours'  },
  { value: '24h', labelKey: 'listings.expiry.24h', label: '24 hours' },
]

const ListingSpotPanel = memo(function ListingSpotPanel({
  location,           // { lat, lng } from SpotMode pin
  onCancel,
  onCreated,          // called with new listing on success
  isImmersiveMode = false,
}) {
  const { t } = useTranslation()
  const { execute, loading } = useAsyncOperation()

  const [title, setTitle]           = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory]     = useState('')
  const [price, setPrice]           = useState('')
  const [quantity, setQuantity]     = useState('')
  const [address, setAddress]       = useState('')
  const [expiresIn, setExpiresIn]   = useState('4h')

  // Auto-fill address from pin location via reverse geocode.
  // A ref prevents re-running when the user manually edits the field.
  const locationKeyRef = useRef(null)
  useEffect(() => {
    if (!location) return
    const key = `${location.lat.toFixed(5)},${location.lng.toFixed(5)}`
    if (key === locationKeyRef.current) return   // same position, don't overwrite
    locationKeyRef.current = key
    reverseGeocode(location.lat, location.lng)
      .then((result) => {
        if (result?.formattedAddress) setAddress(result.formattedAddress)
      })
      .catch(() => {
        setAddress(`${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`)
      })
  }, [location])

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()

    if (!title.trim()) { toast.error(t('listings.titleRequired', 'Title is required')); return }
    if (!category)     { toast.error(t('listings.categoryRequired', 'Please pick a category')); return }
    if (!location)     { toast.error(t('listings.locationRequired', 'Drop a pin first')); return }

    const data = {
      title:       title.trim(),
      description: description.trim() || undefined,
      category,
      price:       price !== '' ? parseFloat(price) : undefined,
      quantity:    quantity.trim() || undefined,
      latitude:    location.lat,
      longitude:   location.lng,
      address:     address.trim() || undefined,
      expires_in:  expiresIn,
    }

    await execute(() => listingAPI.createListing(data), {
      onSuccess: (res) => {
        toast.success(t('listings.posted', 'Listing posted!'))
        onCreated?.(res.data)
        onCancel()
      },
      onError: (err) => {
        logger.error('Failed to post listing', err)
        toast.error(err?.response?.data?.error || t('listings.postFailed', 'Failed to post listing'))
      },
    })
  }, [title, description, category, price, quantity, address, expiresIn, location, execute, t, onCreated, onCancel])

  return (
    <div className={
      isImmersiveMode
        ? 'fixed top-0 left-0 bottom-0 z-[999] w-[360px] bg-white flex flex-col border-r border-gray-200 shadow-xl'
        : 'fixed top-24 right-4 z-[999] w-[min(24rem,calc(100vw-2rem))] bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-primary/20 overflow-hidden'
    }>
      {/* Header */}
      <div className="bg-primary/5 border-b border-primary/20 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <h3 className="text-base font-bold text-gray-900">
          {t('listings.postTitle', "What's available?")}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          onTouchEnd={(e) => { e.preventDefault(); onCancel() }}
          className="text-gray-500 hover:bg-gray-100 rounded-full p-2 transition-colors"
          aria-label={t('common.cancel', 'Cancel')}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Location pin summary */}
      {location && (
        <div className="px-4 py-2 bg-primary/5 border-b border-primary/10 flex items-center gap-2">
          <MapPinIcon className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="text-xs text-primary font-medium">
            {t('listings.pinDropped', 'Pickup location set — drag the pin to adjust')}
          </span>
        </div>
      )}

      {/* Scrollable form */}
      <form
        onSubmit={handleSubmit}
        className={isImmersiveMode ? 'flex-1 overflow-y-auto p-4 space-y-4' : 'p-4 max-h-[calc(100vh-14rem)] overflow-y-auto space-y-4'}
      >
        {/* Category */}
        <div>
          <label className={labelClasses}>{t('listings.category.label', 'Category')}</label>
          <div className="grid grid-cols-4 gap-2 mt-1">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setCategory(cat.id)}
                onTouchEnd={(e) => { e.preventDefault(); setCategory(cat.id) }}
                className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs font-medium transition-all ${
                  category === cat.id
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <cat.Icon className="w-5 h-5 flex-shrink-0" />
                <span className="leading-tight text-center">{t(cat.labelKey, cat.id)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div>
          <label className={labelClasses}>{t('listings.titleLabel', 'Title')}</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('listings.titlePlaceholder', 'e.g. Homemade tamales, Dog walking, Extra tomatoes')}
            maxLength={200}
            className={getInputClasses(false)}
          />
        </div>

        {/* Description */}
        <div>
          <label className={labelClasses}>{t('listings.descriptionLabel', 'Details (optional)')}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder={t('listings.descriptionPlaceholder', 'Any extra details...')}
            className={getTextareaClasses(false)}
          />
        </div>

        {/* Price + Quantity */}
        <div className="flex gap-3">
          <div className="flex-1">
            <label className={labelClasses}>{t('listings.priceLabel', 'Price (blank = free)')}</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
              className={getInputClasses(false)}
            />
          </div>
          <div className="flex-1">
            <label className={labelClasses}>{t('listings.quantityLabel', 'Quantity / slots')}</label>
            <input
              type="text"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder={t('listings.quantityPlaceholder', 'e.g. 12 tamales')}
              className={getInputClasses(false)}
            />
          </div>
        </div>

        {/* Address — read-only, sourced from pin geocode */}
        <div>
          <label className={labelClasses}>
            {t('listings.addressLabel', 'Pickup location')}
          </label>
          <div className="mt-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-600 min-h-[38px]">
            <MapPinIcon className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="flex-1 truncate">
              {address
                ? address
                : t('listings.noAddressYet', 'Move the pin to set location')}
            </span>
          </div>
        </div>

        {/* Expiry */}
        <div>
          <label className={labelClasses}>{t('listings.expiryLabel', 'Available for')}</label>
          <div className="flex gap-2 mt-1">
            {EXPIRY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setExpiresIn(opt.value)}
                onTouchEnd={(e) => { e.preventDefault(); setExpiresIn(opt.value) }}
                className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                  expiresIn === opt.value
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {t(opt.labelKey, opt.label)}
              </button>
            ))}
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          onClick={handleSubmit}
          onTouchEnd={(e) => { e.preventDefault(); handleSubmit(e) }}
          className="w-full py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50 hover:bg-primary/90 transition-colors mt-2"
        >
          {loading ? t('common.posting', 'Posting...') : t('listings.postButton', 'Post listing')}
        </button>
      </form>
    </div>
  )
})

export default ListingSpotPanel
